// Wizard types for clinic creation wizard (6 steps)

export type WizardStep = 'basic' | 'hours' | 'capabilities' | 'onboarding' | 'contact' | 'review';

// Step indices for the 6-step wizard
export const WIZARD_STEPS = {
  BASIC_INFO: 0,
  HOURS: 1,
  CAPABILITIES: 2,
  ONBOARDING: 3,
  CONTACT: 4,
  REVIEW: 5,
} as const;

export const STEP_NAMES = [
  'Basic Info',
  'Operating Hours',
  'Capabilities',
  'Onboarding',
  'Contacts',
  'Review',
];

export const TOTAL_STEPS = 6;

export interface BasicInfo {
  name: string;
  code?: string;
  // Address (merged into basic info for creation wizard — optional for verification wizard compat)
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
  // Contact info
  phone?: string;
  fax?: string;
  email?: string;
  website?: string;
}

// Standalone address interface (used by verification wizard)
export interface Address {
  address1: string;
  address2?: string;
  city: string;
  state: string;
  zip: string;
  country: string;
}

// Operating hours (used by verification wizard)
export interface OperatingHour {
  dayOfWeek: number; // 0=Sunday, 1=Monday, ..., 6=Saturday
  isClosed: boolean;
  openTime: string;  // HH:mm format
  closeTime: string; // HH:mm format
}

// Clinic service (used by verification wizard)
export interface ClinicService {
  serviceProcedureId: number;
  name: string;
  code: string;
  fee?: number;
}

// Default operating hours template (Mon-Fri 8:00-17:00, Sat-Sun closed)
export const getDefaultOperatingHours = (): OperatingHour[] => [
  { dayOfWeek: 0, isClosed: true, openTime: '08:00', closeTime: '17:00' },
  { dayOfWeek: 1, isClosed: false, openTime: '08:00', closeTime: '17:00' },
  { dayOfWeek: 2, isClosed: false, openTime: '08:00', closeTime: '17:00' },
  { dayOfWeek: 3, isClosed: false, openTime: '08:00', closeTime: '17:00' },
  { dayOfWeek: 4, isClosed: false, openTime: '08:00', closeTime: '17:00' },
  { dayOfWeek: 5, isClosed: false, openTime: '08:00', closeTime: '17:00' },
  { dayOfWeek: 6, isClosed: true, openTime: '08:00', closeTime: '17:00' },
];

export const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

// NOTE: there is no per-flag "Capabilities" form state any more. The Emergency
// Room / After Hours / PAT / X-Ray tiles and the fit-test dropdowns were removed
// (Clinic Network decision 2026-09-16); each of those attributes has one home in
// the category / capability / procedure lists below, and the server derives the
// legacy clinic flag columns from those lists.

export interface Contact {
  name: string;
  phone?: string;
  email?: string;
  role: string;
}

export interface Onboarding {
  preferredMethodOfAppointment?: string;
  electronicChainOfCustody?: string; // CSV format
  reportingMethod?: string;
  dotCertification?: boolean;
  numberOfCertifiedDOTProfessionals?: number;
  // translationServices removed (SLV2-3301): it is the 'Translation Services'
  // capability on the Capabilities step; the server derives the clinic column.
  w9OnFile?: boolean;
  eScreenCode?: string;
  formFoxCode?: string;
  questCode?: string;
}

export interface DuplicateMatch {
  id: number;
  name: string;
  address: string;
  phone?: string;
  status: number;
  statusDisplay: string;
  matchScore: number;
  matchReasons: string[];
}

export interface DuplicateCheckResult {
  hasPotentialDuplicates: boolean;
  matches: DuplicateMatch[];
}

export interface WizardState {
  currentStep: number;
  basicInfo: BasicInfo;
  operatingHours: OperatingHour[];
  contact: Contact;
  onboarding: Onboarding;
  // Dynamic lookup selections (ClinicMgmt junction tables + clinic services)
  categoryTypeIds: number[];
  capabilityTypeIds: number[];
  serviceProcedureIds: number[];
  includeCapabilities: boolean;
  includeOnboarding: boolean;
  includeContact: boolean;
  acknowledgedDuplicateWarning: boolean;
  duplicateCheckResult: DuplicateCheckResult | null;
}

export interface ValidationErrors {
  [field: string]: string;
}

export interface StepValidationErrors {
  [step: number]: ValidationErrors;
}

// Helper function to validate basic info step (includes address)
export const validateBasicInfo = (basicInfo: BasicInfo): ValidationErrors => {
  const errors: ValidationErrors = {};

  if (!basicInfo.name.trim()) {
    errors.name = 'Clinic name is required';
  } else if (basicInfo.name.trim().length > 200) {
    errors.name = 'Clinic name must be 200 characters or less';
  }

  // Email validation if provided
  if (basicInfo.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(basicInfo.email)) {
    errors.email = 'Invalid email format';
  }

  // Address validation (required for creation wizard)
  if (!(basicInfo.address1 || '').trim()) {
    errors.address1 = 'Address is required';
  }

  if (!(basicInfo.city || '').trim()) {
    errors.city = 'City is required';
  }

  if (!(basicInfo.state || '').trim()) {
    errors.state = 'State is required';
  } else if ((basicInfo.state || '').length !== 2) {
    errors.state = 'State must be 2-character code';
  }

  if (!(basicInfo.zip || '').trim()) {
    errors.zip = 'ZIP code is required';
  } else if (!/^\d{5}(-\d{4})?$/.test(basicInfo.zip || '')) {
    errors.zip = 'ZIP code must be 5 digits or ZIP+4 format';
  }

  return errors;
};

// Helper function to validate contact step (all optional)
export const validateContact = (
  contact: Contact,
  includeContact: boolean
): ValidationErrors => {
  const errors: ValidationErrors = {};

  if (includeContact) {
    // Email validation if provided
    if (contact.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.email)) {
      errors.email = 'Invalid email format';
    }
  }

  return errors;
};

// Helper function to validate the operating-hours step.
// Error keys follow OperatingHoursForm's lookup convention: `day{N}_time`.
export const validateOperatingHours = (hours: OperatingHour[]): ValidationErrors => {
  const errors: ValidationErrors = {};

  for (const hour of hours) {
    if (!hour.isClosed && hour.openTime >= hour.closeTime) {
      errors[`day${hour.dayOfWeek}_time`] = 'Close time must be after open time';
    }
  }

  return errors;
};

// Helper function to validate onboarding step (optional step)
export const validateOnboarding = (
  onboarding: Onboarding,
  includeOnboarding: boolean
): ValidationErrors => {
  const errors: ValidationErrors = {};

  if (includeOnboarding) {
    if (onboarding.dotCertification && !onboarding.numberOfCertifiedDOTProfessionals) {
      errors.numberOfCertifiedDOTProfessionals = 'Number of DOT professionals is required when DOT certification is enabled';
    }

    // Mirrors the API's [Range(0, 999)] so the user sees this here instead of a 400.
    if (onboarding.dotCertification && onboarding.numberOfCertifiedDOTProfessionals !== undefined
        && (onboarding.numberOfCertifiedDOTProfessionals < 1 || onboarding.numberOfCertifiedDOTProfessionals > 999)) {
      errors.numberOfCertifiedDOTProfessionals = 'Number of DOT professionals must be between 1 and 999';
    }
  }

  return errors;
};
